import { countOperatingDays, segmentMonthlyTarget, type SalesTargetSegment } from './salesTarget';

export interface DailySalesRow {
  date: string;
  sales: number;
  customerCount: number;
  txnCount: number;
  lunchSales: number;
  lunchCustomerCount: number;
  lunchTxnCount: number;
  dinnerSales: number;
  dinnerCustomerCount: number;
  dinnerTxnCount: number;
}

export interface MonthlySalesRow extends Omit<DailySalesRow, 'date'> {
  periodKey: string;
}

export interface SalesPeriodPoint {
  key: string;
  label: string;
  sales: number | null;
  customers: number | null;
  target: number | null;
  excluded: boolean;
}

export interface SalesPeriodPanel {
  key: string;
  label: string;
  points: SalesPeriodPoint[];
  sales: number;
  customers: number;
  target: number;
  hasData: boolean;
  excludedEvents: number;
  current: boolean;
  future: boolean;
}

type Segment = SalesTargetSegment;

const segmentTotals = (row: DailySalesRow | MonthlySalesRow, segment: Segment) => {
  if (segment === 'lunch') return { sales: Number(row.lunchSales) || 0, customers: Number(row.lunchCustomerCount) || 0, txns: Number(row.lunchTxnCount) || 0 };
  if (segment === 'dinner') return { sales: Number(row.dinnerSales) || 0, customers: Number(row.dinnerCustomerCount) || 0, txns: Number(row.dinnerTxnCount) || 0 };
  return { sales: Number(row.sales) || 0, customers: Number(row.customerCount) || 0, txns: Number(row.txnCount) || 0 };
};

const monthDates = (yearMonth: string): string[] => {
  const [year, month] = yearMonth.split('-').map(Number);
  const lastDay = new Date(Date.UTC(year, month, 0)).getUTCDate();
  return Array.from({ length: lastDay }, (_, index) => `${yearMonth}-${String(index + 1).padStart(2, '0')}`);
};

const isOperatingDay = (date: string): boolean => new Date(`${date}T00:00:00Z`).getUTCDay() !== 1;

export const summaryMonth = (row: MonthlySalesRow): string => row.periodKey.replace(/^MONTH#/, '');

export const priorYearLunchRatio = (year: number, summaries: Map<string, MonthlySalesRow>): { ratio: number; months: number } => {
  let lunch = 0;
  let dinner = 0;
  let months = 0;
  for (let month = 1; month <= 12; month += 1) {
    const row = summaries.get(`${year - 1}-${String(month).padStart(2, '0')}`);
    if (!row) continue;
    const lunchSales = Number(row.lunchSales) || 0;
    const dinnerSales = Number(row.dinnerSales) || 0;
    lunch += lunchSales;
    dinner += dinnerSales;
    if (lunchSales + dinnerSales > 0) months += 1;
  }
  return { ratio: lunch + dinner > 0 ? lunch / (lunch + dinner) : .5, months };
};

const dailyTarget = (yearMonth: string, storeId: string, segment: Segment, lunchRatio: number): number => {
  const operatingDays = countOperatingDays(yearMonth);
  return operatingDays > 0 ? segmentMonthlyTarget(storeId, segment, lunchRatio) / operatingDays : 0;
};

export const buildMonthlyPanel = ({
  yearMonth, rows, events, storeId, segment, lunchRatio, today, openDate,
}: {
  yearMonth: string;
  rows: DailySalesRow[] | undefined;
  events: Set<string>;
  storeId: string;
  segment: Segment;
  lunchRatio: number;
  today: string;
  openDate?: string | null;
}): SalesPeriodPanel => {
  const byDate = new Map(rows?.map((row) => [row.date, row]) ?? []);
  const targetPerDay = dailyTarget(yearMonth, storeId, segment, lunchRatio);
  const points = monthDates(yearMonth).map((date): SalesPeriodPoint => {
    const event = events.has(date);
    const row = byDate.get(date);
    const totals = row ? segmentTotals(row, segment) : null;
    const eligible = date <= today && (!openDate || date >= openDate);
    return {
      key: date,
      label: String(Number(date.slice(-2))),
      sales: eligible && !event && totals && totals.txns > 0 ? totals.sales : null,
      customers: eligible && !event && totals && totals.txns > 0 ? totals.customers : null,
      target: eligible && !event && isOperatingDay(date) ? targetPerDay : null,
      excluded: event,
    };
  });
  return {
    key: yearMonth,
    label: `${Number(yearMonth.slice(5))}月`,
    points,
    sales: points.reduce((sum, point) => sum + (point.sales ?? 0), 0),
    customers: points.reduce((sum, point) => sum + (point.customers ?? 0), 0),
    target: points.reduce((sum, point) => sum + (point.target ?? 0), 0),
    hasData: points.some((point) => point.sales !== null),
    excludedEvents: points.filter((point) => point.excluded).length,
    current: yearMonth === today.slice(0, 7),
    future: yearMonth > today.slice(0, 7),
  };
};

export const buildAnnualPanel = ({
  year, summaries, eventDays, events, storeId, segment, lunchRatio, today, openDate,
}: {
  year: number;
  summaries: Map<string, MonthlySalesRow>;
  eventDays: Map<string, DailySalesRow[]>;
  events: Set<string>;
  storeId: string;
  segment: Segment;
  lunchRatio: number;
  today: string;
  openDate?: string | null;
}): SalesPeriodPanel => {
  let excludedEvents = 0;
  const points = Array.from({ length: 12 }, (_, index): SalesPeriodPoint => {
    const yearMonth = `${year}-${String(index + 1).padStart(2, '0')}`;
    const summary = summaries.get(yearMonth);
    const raw = summary ? segmentTotals(summary, segment) : null;
    const eventRows = new Map(eventDays.get(yearMonth)?.map((row) => [row.date, row]) ?? []);
    const eventDates = monthDates(yearMonth).filter((date) => events.has(date) && (!openDate || date >= openDate) && date <= today);
    excludedEvents += eventDates.length;
    const eventTotals = eventDates.reduce((sum, date) => {
      const row = eventRows.get(date);
      if (!row) return sum;
      const totals = segmentTotals(row, segment);
      return { sales: sum.sales + totals.sales, customers: sum.customers + totals.customers, txns: sum.txns + totals.txns };
    }, { sales: 0, customers: 0, txns: 0 });
    const daily = dailyTarget(yearMonth, storeId, segment, lunchRatio);
    const target = monthDates(yearMonth).reduce((sum, date) =>
      sum + (date <= today && (!openDate || date >= openDate) && !events.has(date) && isOperatingDay(date) ? daily : 0), 0);
    const hasData = raw !== null && raw.txns - eventTotals.txns > 0;
    return {
      key: yearMonth,
      label: `${index + 1}月`,
      sales: hasData ? raw.sales - eventTotals.sales : null,
      customers: hasData ? raw.customers - eventTotals.customers : null,
      target: yearMonth <= today.slice(0, 7) && (!openDate || yearMonth >= openDate.slice(0, 7)) ? target : null,
      excluded: eventDates.length > 0,
    };
  });
  return {
    key: String(year),
    label: `${year}年`,
    points,
    sales: points.reduce((sum, point) => sum + (point.sales ?? 0), 0),
    customers: points.reduce((sum, point) => sum + (point.customers ?? 0), 0),
    target: points.reduce((sum, point) => sum + (point.target ?? 0), 0),
    hasData: points.some((point) => point.sales !== null),
    excludedEvents,
    current: year === Number(today.slice(0, 4)),
    future: year > Number(today.slice(0, 4)),
  };
};
